// libraries
import React from 'react'
import { inject, observer } from 'mobx-react'
import { debounce } from 'lodash/function'

// styles
import './index.scss'

// material-ui
import { CircularProgress, Grid, List, ListItem, ListItemIcon, ListItemText, TextField } from '@material-ui/core'

// custom components
import CustomAvatar from '../../basic/customAvatar'

// api
import { api } from '../../../functions/api'

// components
import { mdiArrowLeft, mdiBarcodeScan, mdiContentSave, mdiCursorText, mdiDownload } from '@mdi/js'
import Icon from '@mdi/react'

import { BarcodeScanner } from '../scanner'

import noPhoto from '../../../assets/image/no-product-warehouse-photo.png'

@inject('internal')
@inject('translate')
@observer
export default class ProductWarehouseScanner extends React.Component {
  constructor(props) {
    super(props)
    
    this.state = this.getInitState()
  }
  
  getInitState = () => {
    return {
      loadingProduct: false,
      step: 'intro',
      ean: null,
      product: null,
      error: '',
      pieces: 1,
      serialNumberScan: false,
      serialNumberInput: false,
      serialNumberMissing: false,
      serialNumberPicker: true,
      serialNumber: null,
      isSerialNumberValid: false,
      finalConfirm: false,
    }
  }
  
  changeStep = (step, other = {}) => {
    this.setState({ step, ...other })
  }
  
  handleEANInput = result => {
    const { ean } = this.state
    if (ean === null || ean !== result) {
      this.setState({ ean: result })
    }
  }
  
  handleProductLoadByEAN = debounce(async () => {
    this.setState({ loadingProduct: true, error: '' }, () => {
      const { ean } = this.state
      api({
        url: 'products/get-by-ean',
        method: 'post',
        authorization: this.props.internal.user.access_token,
        params: {
          ean,
          ...(this.props.saveOther || {}),
        },
      }).then(result => {
        this.setState({ loadingProduct: false })
        if (result.success) {
          this.setState({
            product: result.data.data,
            step: 'confirmProduct',
          })
          
          return
        }
        
        this.setState({ error: result.data.message })
      })
    })
  }, 750)
  
  handleSerialNumberInput = debounce(async serialNumber => {
    this.setState({
      serialNumber,
      isSerialNumberValid: serialNumber.length >= 9,
    })
  }, 500)
  
  handlePiecesInput = debounce(async pieces => {
    this.setState({
      pieces: Number(pieces),
    })
  }, 500)
  
  handleSave = () => {
    this.setState({ loadingFinish: true, error: '' }, () => {
      const { product, serialNumber, pieces } = this.state
      const { mode } = this.props
      const params = {
        serial_number: serialNumber,
        pieces,
        mode,
        ...(this.props.saveOther || {}),
      }
      
      api({
        url: `products/${product.id}/warehouse-movement`,
        method: 'post',
        authorization: this.props.internal.user.access_token,
        params,
      }).then(result => {
        this.setState({ loadingFinish: false })
        if (result.success) {
          if (this.props.handleAfterSave) {
            this.props.handleAfterSave()
          }
          
          if (this.props.resetAfterSave) {
            this.setState({ ...this.getInitState() })
          }
          
          this.props.internal.setSnackbar(result.data.message, true)
          
          return
        }
        
        if (result.data.data.hasOwnProperty('errors')) {
          this.props.internal.setSnackbar(Object.values(result.data.data.errors)[0][0], true, true)
        } else {
          this.props.internal.setSnackbar(Object.values(result.data.message), true, true)
        }
      })
    })
  }
  
  render() {
    const { step, loadingProduct, error, isSerialNumberValid, finalConfirm } = this.state
    const { ean, product, serialNumber, pieces } = this.state
    const { serialNumberPicker, serialNumberScan, serialNumberInput, serialNumberMissing } = this.state
    
    if (step === 'intro') {
      return (
        <Grid container spacing={16}>
          <Grid item xs={12}>
            <List>
              <ListItem
                button
                className={'justText btn-orange-bg border-radius-18 mb-2'}
                onClick={() => this.changeStep('scanEAN')}
              >
                <ListItemIcon>
                  <Icon path={mdiBarcodeScan} size={1}/>
                </ListItemIcon>
                {this.props.translate.i18n.MODULES.PRODUCT_WAREHOUSE_SCAN.SCAN_EAN}
              </ListItem>
              <ListItem
                button
                className={'justText btn-orange-bg border-radius-18 mb-2'}
                onClick={() => this.changeStep('inputEAN')}
              >
                <ListItemIcon>
                  <Icon path={mdiCursorText} size={1}/>
                </ListItemIcon>
                {this.props.translate.i18n.MODULES.PRODUCT_WAREHOUSE_SCAN.INPUT_EAN}
              </ListItem>
            </List>
          </Grid>
        </Grid>
      )
    }
    
    if (step === 'scanEAN') {
      return (
        <Grid container spacing={16}>
          <Grid item xs={12}>
            <List>
              {ean ? (
                <ListItem>
                  {this.props.translate.i18n.MODULES.PRODUCT_WAREHOUSE_SCAN.SCANNED_EAN}: {ean}
                </ListItem>
              ) : (
                <ListItem>
                  <BarcodeScanner
                    onScanned={this.handleEANInput}
                    codeType="EAN_13"
                  />
                </ListItem>
              )}
              {loadingProduct ? <ListItem><CircularProgress/></ListItem> : null}
              {error ? <ListItem>{error}</ListItem> : null}
              <ListItem
                button
                className={'justText btn-orange-bg border-radius-18 mb-2'}
                onClick={this.handleProductLoadByEAN}
                disabled={! ean || loadingProduct}
              >
                <ListItemIcon>
                  <Icon path={mdiDownload} size={1}/>
                </ListItemIcon>
                {this.props.translate.i18n.MODULES.PRODUCT_WAREHOUSE_SCAN.LOAD_PRODUCT}
              </ListItem>
              <ListItem
                button
                className={'justText btn-orange-bg border-radius-18 mb-2'}
                onClick={() => this.changeStep('intro', { ean: null, error: '' })}
              >
                <ListItemIcon>
                  <Icon path={mdiArrowLeft} size={1}/>
                </ListItemIcon>
                {this.props.translate.i18n.GLOBAL.BUTTONS.BACK}
              </ListItem>
            </List>
          </Grid>
        </Grid>
      )
    }
    
    if (step === 'inputEAN') {
      return (
        <Grid container spacing={16}>
          <Grid item xs={12}>
            <List>
              <ListItem>
                <TextField
                  autoFocus
                  label={this.props.translate.i18n.MODULES.PRODUCTS.CODE}
                  id="ean"
                  className={'info-field'}
                  fullWidth
                  onChange={event => this.handleEANInput(event.target.value)}
                />
              </ListItem>
              {loadingProduct ? <ListItem><CircularProgress/></ListItem> : null}
              {error ? <ListItem>{error}</ListItem> : null}
              <ListItem
                button
                className={'justText btn-orange-bg border-radius-18 mb-2'}
                onClick={() => this.handleProductLoadByEAN(ean)}
                disabled={! ean || loadingProduct}
              >
                <ListItemIcon>
                  <Icon path={mdiDownload} size={1}/>
                </ListItemIcon>
                {this.props.translate.i18n.MODULES.PRODUCT_WAREHOUSE_SCAN.LOAD_PRODUCT}
              </ListItem>
              <ListItem
                button
                className={'justText btn-orange-bg border-radius-18 mb-2'}
                onClick={() => this.changeStep('intro', { ean: null, error: '' })}
              >
                <ListItemIcon>
                  <Icon path={mdiArrowLeft} size={1}/>
                </ListItemIcon>
                {this.props.translate.i18n.GLOBAL.BUTTONS.BACK}
              </ListItem>
            </List>
          </Grid>
        </Grid>
      )
    }
    
    if (step === 'confirmProduct') {
      return (
        <React.Fragment>
          <Grid container spacing={16}>
            <Grid item xs={12} sm={4}>
              <CustomAvatar
                alt={product.name}
                src={product.asset_warehouse_photo || noPhoto}
                disableEdit
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={this.props.translate.i18n.MODULES.PRODUCTS.CODE}
                    secondary={product.code}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={this.props.translate.i18n.MODULES.PRODUCTS.NAME}
                    secondary={product.name}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={this.props.translate.i18n.MODULES.PRODUCTS.WAREHOUSE_LOCATION}
                    secondary={product.warehouse_location || 'N/A'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={this.props.translate.i18n.MODULES.PRODUCTS.WAREHOUSE_INFO_REAL}
                    secondary={product.warehouse_info_real}
                  />
                </ListItem>
              </List>
            </Grid>
            {serialNumberPicker ? (
              <React.Fragment>
                <Grid item xs={12}>
                  {this.props.translate.i18n.MODULES.PRODUCT_WAREHOUSE_SCAN.SERIAL_NUMBER_INFO}
                </Grid>
                <Grid item xs={12}>
                  <List>
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={() => this.setState({ serialNumberPicker: false, serialNumberScan: true }, () => {
                        setTimeout(() => {
                          const elem = document.getElementById('storage-scroll-wrapper')
                          elem.scrollTo({
                            top: elem.scrollHeight - elem.clientHeight - 50,
                            behavior: 'smooth',
                          })
                        }, 1000)
                      })}
                    >
                      {this.props.translate.i18n.MODULES.PRODUCT_WAREHOUSE_SCAN.SCAN_SERIAL_NUMBER}
                    </ListItem>
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={() => this.setState({ serialNumberPicker: false, serialNumberInput: true })}
                    >
                      {this.props.translate.i18n.MODULES.PRODUCT_WAREHOUSE_SCAN.INPUT_SERIAL_NUMBER}
                    </ListItem>
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={() => this.setState({ serialNumberPicker: false, serialNumberMissing: true })}
                    >
                      {this.props.translate.i18n.MODULES.PRODUCT_WAREHOUSE_SCAN.MISSING_SERIAL_NUMBER}
                    </ListItem>
                  </List>
                </Grid>
              </React.Fragment>
            ) : null}
            {serialNumberScan ? (
              <Grid container spacing={16}>
                <Grid item xs={12}>
                  <List>
                    {serialNumber && isSerialNumberValid ? (
                      <ListItem>
                        {this.props.translate.i18n.MODULES.PRODUCT_WAREHOUSE_SCAN.SCANNED_SERIAL_NUMBER}: {serialNumber}
                      </ListItem>
                    ) : (
                      <ListItem>
                        <BarcodeScanner
                          onScanned={this.handleSerialNumberInput}
                          codeType="CODE_128"
                        />
                      </ListItem>
                    )}
                    {serialNumber && ! isSerialNumberValid ?
                      <ListItem>{this.props.translate.i18n.MODULES.ISSUE_CARDS.INVALID_SERIAL_NUMBER}</ListItem> : null}
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={() => this.setState({ serialNumberScan: false, finalConfirm: true })}
                      disabled={! serialNumber || ! isSerialNumberValid}
                    >
                      <ListItemIcon>
                        <Icon path={mdiDownload} size={1}/>
                      </ListItemIcon>
                      {this.props.translate.i18n.GLOBAL.BUTTONS.CONFIRM}
                    </ListItem>
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={() => this.setState({
                        serialNumber: null,
                        serialNumberScan: false,
                        serialNumberPicker: true,
                      })}
                    >
                      <ListItemIcon>
                        <Icon path={mdiArrowLeft} size={1}/>
                      </ListItemIcon>
                      {this.props.translate.i18n.GLOBAL.BUTTONS.BACK}
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            ) : null}
            {serialNumberInput ? (
              <Grid container spacing={16}>
                <Grid item xs={12}>
                  <List>
                    <ListItem>
                      <TextField
                        autoFocus
                        label={this.props.translate.i18n.MODULES.PRODUCTS.SERIAL_NUMBER}
                        id="serial_number"
                        className={'info-field'}
                        fullWidth
                        onChange={event => this.handleSerialNumberInput(event.target.value)}
                      />
                    </ListItem>
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={() => this.setState({ serialNumberInput: false, finalConfirm: true })}
                      disabled={! serialNumber || ! isSerialNumberValid}
                    >
                      <ListItemIcon>
                        <Icon path={mdiDownload} size={1}/>
                      </ListItemIcon>
                      {this.props.translate.i18n.GLOBAL.BUTTONS.CONFIRM}
                    </ListItem>
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={() => this.setState({
                        serialNumber: null,
                        serialNumberInput: false,
                        serialNumberPicker: true,
                      })}
                    >
                      <ListItemIcon>
                        <Icon path={mdiArrowLeft} size={1}/>
                      </ListItemIcon>
                      {this.props.translate.i18n.GLOBAL.BUTTONS.BACK}
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            ) : null}
            {serialNumberMissing ? (
              <Grid container spacing={16}>
                <Grid item xs={12}>
                  <List>
                    <ListItem>
                      <TextField
                        autoFocus
                        label={this.props.translate.i18n.MODULES.PRODUCTS.PIECES}
                        id="pieces"
                        className={'info-field'}
                        type="number"
                        fullWidth
                        onChange={event => this.handlePiecesInput(event.target.value)}
                      />
                    </ListItem>
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={() => this.setState({ serialNumberMissing: false, finalConfirm: true })}
                      disabled={pieces < 1}
                    >
                      <ListItemIcon>
                        <Icon path={mdiDownload} size={1}/>
                      </ListItemIcon>
                      {this.props.translate.i18n.GLOBAL.BUTTONS.CONFIRM}
                    </ListItem>
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={() => this.setState({
                        serialNumber: null,
                        pieces: 1,
                        serialNumberMissing: false,
                        serialNumberPicker: true,
                      })}
                    >
                      <ListItemIcon>
                        <Icon path={mdiArrowLeft} size={1}/>
                      </ListItemIcon>
                      {this.props.translate.i18n.GLOBAL.BUTTONS.BACK}
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            ) : null}
            {finalConfirm ? (
              <Grid container spacing={16}>
                <Grid item xs={12}>
                  <List dense>
                    {serialNumber ? (
                      <ListItem>
                        <ListItemText
                          primary={this.props.translate.i18n.MODULES.PRODUCTS.SERIAL_NUMBER}
                          secondary={serialNumber}
                        />
                      </ListItem>
                    ) : null}
                    {! serialNumber ? (
                      <ListItem>
                        <ListItemText
                          primary={this.props.translate.i18n.MODULES.PRODUCTS.PIECES}
                          secondary={pieces}
                        />
                      </ListItem>
                    ) : null}
                  </List>
                  <List>
                    <ListItem>
                      {'from_storage' === this.props.mode ? 'Prajete si vyskladniť produkt?' : 'Prajete si naskladniť produkt?'}
                    </ListItem>
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={this.handleSave}
                    >
                      <ListItemIcon>
                        <Icon path={mdiContentSave} size={1}/>
                      </ListItemIcon>
                      {this.props.translate.i18n.GLOBAL.BUTTONS.YES}
                    </ListItem>
                    <ListItem
                      button
                      className={'justText btn-orange-bg border-radius-18 mb-2'}
                      onClick={() => this.setState({
                        serialNumber: null,
                        pieces: 1,
                        serialNumberPicker: true,
                        finalConfirm: false,
                      })}
                    >
                      <ListItemIcon>
                        <Icon path={mdiArrowLeft} size={1}/>
                      </ListItemIcon>
                      {this.props.translate.i18n.GLOBAL.BUTTONS.BACK}
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            ) : null}
          </Grid>
        </React.Fragment>
      )
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/complex/ProductWarehouseScanner/index.js